import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import { useLanguage } from '../../contexts/LanguageContext';
import { VoucherDto } from '../../types/vouchers.types';
import { VOUCHER_STATUS } from '../../constants/vouchers.constants';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';

interface VoucherPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  voucher: VoucherDto | null;
}

const VoucherPreviewDialog: React.FC<VoucherPreviewDialogProps> = ({
  open,
  onClose,
  voucher
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (open) {
      loadCompanyInfo();
    }
  }, [open]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  if (!voucher) return null;

  const isDraft = voucher.status === VOUCHER_STATUS.DRAFT.code;
  const debitDetails = voucher.details.filter(d => d.side?.toLowerCase() === 'debit');
  const creditDetails = voucher.details.filter(d => d.side?.toLowerCase() === 'credit');
  const totalDebit = debitDetails.reduce((sum, d) => sum + d.allocatedAmount, 0);
  const totalCredit = creditDetails.reduce((sum, d) => sum + d.allocatedAmount, 0);

  const voucherTypeNames: Record<string, { en: string; ar: string }> = {
    CR: { en: 'Cash Receipt', ar: 'سند قبض نقدي' },
    CP: { en: 'Cash Payment', ar: 'سند صرف نقدي' },
    CHR: { en: 'Check Receipt', ar: 'سند قبض بشيك' },
    CHP: { en: 'Check Payment', ar: 'سند صرف بشيك' }
  };

  const voucherName = voucherTypeNames[voucher.voucherType] || { en: 'Voucher', ar: 'سند' };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;

    printWindow.document.write(`
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${voucher.voucherNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; direction: ${isRTL ? 'rtl' : 'ltr'}; color: #333; }
          .header { text-align: center; border-bottom: 3px solid #667eea; padding-bottom: 15px; margin-bottom: 20px; }
          .header img { max-height: 60px; margin-bottom: 8px; }
          .header h2 { color: #667eea; margin: 5px 0; }
          .header .subtitle { font-size: 12px; color: #666; }
          .voucher-title { text-align: center; margin-bottom: 20px; }
          .voucher-title h3 { color: #667eea; font-size: 22px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; border: 2px solid #667eea; border-radius: 8px; padding: 15px; margin-bottom: 20px; }
          .info-item label { font-size: 11px; color: #888; display: block; }
          .info-item span { font-weight: bold; font-size: 14px; }
          .status-badge { display: inline-block; padding: 2px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; color: white; }
          .status-posted { background: #4caf50; }
          .status-draft { background: #ff9800; }
          .check-info { background: #f3e5f5; border-radius: 6px; padding: 10px 15px; margin-bottom: 15px; }
          .check-info label { font-size: 11px; color: #888; font-weight: bold; display: block; margin-bottom: 5px; }
          .check-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
          .check-grid span { font-size: 13px; }
          .section { margin-bottom: 15px; border: 1px solid #ddd; border-radius: 6px; overflow: hidden; }
          .section-header { padding: 8px 15px; color: white; font-weight: bold; font-size: 14px; }
          .section-header.debit { background: #4caf50; }
          .section-header.credit { background: #ff9800; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #f5f5f5; padding: 8px 10px; font-size: 12px; text-align: ${isRTL ? 'right' : 'left'}; border-bottom: 2px solid #ddd; }
          th.amount { text-align: ${isRTL ? 'left' : 'right'}; }
          td { padding: 8px 10px; font-size: 12px; border-bottom: 1px solid #eee; }
          td.amount { text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; }
          td.amount.debit { color: #4caf50; }
          td.amount.credit { color: #ff9800; }
          .account-code { font-weight: bold; }
          .account-name { font-size: 11px; color: #666; }
          .total-row td { background: rgba(0,0,0,0.05); font-weight: bold; font-size: 13px; }
          .balance-box { border: 2px solid #667eea; border-radius: 8px; padding: 12px 20px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
          .balance-box h4 { font-size: 16px; }
          .balance-check { font-size: 24px; }
          .notes-box { background: #f9f9f9; border-radius: 4px; padding: 8px; margin-bottom: 15px; }
          .notes-box label { font-size: 11px; color: #888; font-weight: bold; }
          .notes-box p { font-size: 13px; margin-top: 3px; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .sig-block { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #333; padding-top: 8px; margin-top: 60px; font-weight: bold; font-size: 13px; }
          @media print { @page { size: A4; margin: 1cm; } }
        </style>
      </head>
      <body>
        ${companyInfo ? `
        <div class="header">
          ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
          <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
          <div class="subtitle">
            ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
          </div>
        </div>
        ` : ''}

        <div class="voucher-title">
          <h3>${isRTL ? voucherName.ar : voucherName.en}</h3>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <label>${isRTL ? 'رقم السند' : 'Voucher Number'}</label>
            <span style="color: #667eea;">${voucher.voucherNumber}</span>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'التاريخ' : 'Date'}</label>
            <span>${new Date(voucher.voucherDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</span>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'الحالة' : 'Status'}</label>
            <span class="status-badge status-${voucher.status.toLowerCase()}">${isDraft ? (isRTL ? 'مسودة' : 'Draft') : (isRTL ? 'مرحّل' : 'Posted')}</span>
            ${voucher.journalEntryNumber ? `<span style="display:inline-block;padding:2px 10px;border-radius:12px;font-size:12px;font-weight:bold;color:#0288d1;border:1px solid #0288d1;margin-${isRTL ? 'right' : 'left'}:6px;">${voucher.journalEntryNumber} (#${voucher.journalEntryId})</span>` : ''}
          </div>
          ${voucher.payeeName ? `
          <div class="info-item">
            <label>${isRTL ? (voucher.voucherType.includes('R') ? 'الدافع' : 'المستفيد') : (voucher.voucherType.includes('R') ? 'Payer' : 'Beneficiary')}</label>
            <span style="color: #4caf50;">${voucher.payeeName}</span>
          </div>
          ` : ''}
        </div>

        ${voucher.chequeNumber ? `
        <div class="check-info">
          <label>${isRTL ? 'معلومات الشيك' : 'Check Details'}</label>
          <div class="check-grid">
            <span><strong>${isRTL ? 'رقم:' : 'Number:'}</strong> ${voucher.chequeNumber}</span>
            ${voucher.chequeDate ? `<span><strong>${isRTL ? 'التاريخ:' : 'Date:'}</strong> ${new Date(voucher.chequeDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</span>` : ''}
            ${voucher.bankName ? `<span><strong>${isRTL ? 'البنك:' : 'Bank:'}</strong> ${voucher.bankName}</span>` : ''}
          </div>
        </div>
        ` : ''}

        ${voucher.notes ? `
        <div class="notes-box">
          <label>${isRTL ? 'ملاحظات:' : 'Notes:'}</label>
          <p>${voucher.notes}</p>
        </div>
        ` : ''}

        <div class="section">
          <div class="section-header debit">${isRTL ? '📥 المدين (Debit)' : '📥 Debit'}</div>
          <table>
            <thead>
              <tr>
                <th>${isRTL ? 'الحساب' : 'Account'}</th>
                <th>${isRTL ? 'مركز التكلفة' : 'Cost Center'}</th>
                <th>${isRTL ? 'الوصف' : 'Description'}</th>
                <th class="amount">${isRTL ? 'المبلغ' : 'Amount'}</th>
              </tr>
            </thead>
            <tbody>
              ${debitDetails.map(d => `
              <tr>
                <td><span class="account-code">${d.accountCode}</span><br/><span class="account-name">${isRTL ? d.accountNameAr || d.accountName : d.accountName}</span></td>
                <td>${d.costCenterCode ? `<span class="account-code">${d.costCenterCode}</span>${(isRTL ? d.costCenterNameAr || d.costCenterName : d.costCenterName) ? `<br/><span class="account-name">${isRTL ? d.costCenterNameAr || d.costCenterName : d.costCenterName}</span>` : ''}` : '-'}</td>
                <td>${d.description || '-'}</td>
                <td class="amount debit">${d.allocatedAmount.toFixed(3)}</td>
              </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="3" style="text-align: ${isRTL ? 'left' : 'right'};">${isRTL ? 'إجمالي المدين:' : 'Total Debit:'}</td>
                <td class="amount debit">${totalDebit.toFixed(3)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="section">
          <div class="section-header credit">${isRTL ? '📤 الدائن (Credit)' : '📤 Credit'}</div>
          <table>
            <thead>
              <tr>
                <th>${isRTL ? 'الحساب' : 'Account'}</th>
                <th>${isRTL ? 'مركز التكلفة' : 'Cost Center'}</th>
                <th>${isRTL ? 'الوصف' : 'Description'}</th>
                <th class="amount">${isRTL ? 'المبلغ' : 'Amount'}</th>
              </tr>
            </thead>
            <tbody>
              ${creditDetails.map(d => `
              <tr>
                <td><span class="account-code">${d.accountCode}</span><br/><span class="account-name">${isRTL ? d.accountNameAr || d.accountName : d.accountName}</span></td>
                <td>${d.costCenterCode ? `<span class="account-code">${d.costCenterCode}</span>${(isRTL ? d.costCenterNameAr || d.costCenterName : d.costCenterName) ? `<br/><span class="account-name">${isRTL ? d.costCenterNameAr || d.costCenterName : d.costCenterName}</span>` : ''}` : '-'}</td>
                <td>${d.description || '-'}</td>
                <td class="amount credit">${d.allocatedAmount.toFixed(3)}</td>
              </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="3" style="text-align: ${isRTL ? 'left' : 'right'};">${isRTL ? 'إجمالي الدائن:' : 'Total Credit:'}</td>
                <td class="amount credit">${totalCredit.toFixed(3)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="balance-box">
          <h4>${isRTL ? 'التوازن' : 'Balance'}</h4>
          <div>
            ${totalDebit.toFixed(3)} = ${totalCredit.toFixed(3)}
            <span class="balance-check">${Math.abs(totalDebit - totalCredit) < 0.01 ? ' ✅' : ' ❌'}</span>
          </div>
        </div>

        <div class="signatures">
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المدير المالي' : 'Financial Manager'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المدير العام' : 'General Manager'}</div></div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // printWindow stays open for user review
    }, 500);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDarkMode ? '#1a1a2e' : 'white'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: isDarkMode
            ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Box component="span">
          {isRTL ? 'معاينة السند' : 'Voucher Preview'}
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {/* Header Info */}
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'رقم السند' : 'Voucher Number'}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {voucher.voucherNumber}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'التاريخ' : 'Date'}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {new Date(voucher.voucherDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'الحالة' : 'Status'}
              </Typography>
              <Box>
                <Chip
                  label={isRTL
                    ? (isDraft ? VOUCHER_STATUS.DRAFT.nameAr : VOUCHER_STATUS.POSTED.nameAr)
                    : (isDraft ? VOUCHER_STATUS.DRAFT.nameEn : VOUCHER_STATUS.POSTED.nameEn)
                  }
                  color={isDraft ? 'warning' : 'success'}
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
                {voucher.journalEntryNumber && (
                  <Chip label={`${voucher.journalEntryNumber} (#${voucher.journalEntryId})`} size="small" color="info" variant="outlined" sx={{ ml: 0.5 }} />
                )}
              </Box>
            </Box>
            {voucher.payeeName && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? (voucher.voucherType.includes('R') ? 'الدافع' : 'المستفيد') : (voucher.voucherType.includes('R') ? 'Payer' : 'Beneficiary')}
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  {voucher.payeeName}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Check Details */}
          {voucher.chequeNumber && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(118, 75, 162, 0.1)', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', mb: 1 }}>
                {isRTL ? 'معلومات الشيك' : 'Check Details'}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                <Typography variant="body2">
                  <strong>{isRTL ? 'رقم:' : 'Number:'}</strong> {voucher.chequeNumber}
                </Typography>
                {voucher.chequeDate && (
                  <Typography variant="body2">
                    <strong>{isRTL ? 'التاريخ:' : 'Date:'}</strong> {new Date(voucher.chequeDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}
                  </Typography>
                )}
                {voucher.bankName && (
                  <Typography variant="body2">
                    <strong>{isRTL ? 'البنك:' : 'Bank:'}</strong> {voucher.bankName}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {voucher.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                {isRTL ? 'ملاحظات:' : 'Notes:'}
              </Typography>
              <Typography variant="body2">
                {voucher.notes}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Debit Section */}
        <Paper
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: isDarkMode ? 'rgba(76,175,80,0.1)' : 'white'
          }}
        >
          <Box sx={{ bgcolor: isDarkMode ? 'rgba(76,175,80,0.3)' : '#4caf50', color: 'white', p: 1.5, fontWeight: 'bold' }}>
            {isRTL ? '📥 المدين' : '📥 Debit'}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الحساب' : 'Account'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'مركز التكلفة' : 'Cost Center'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الوصف' : 'Description'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'المبلغ' : 'Amount'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {debitDetails.map((detail, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <strong>{detail.accountCode}</strong>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {isRTL ? detail.accountNameAr : detail.accountName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {detail.costCenterCode ? (
                        <>
                          {detail.costCenterCode}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {isRTL ? detail.costCenterNameAr : detail.costCenterName}
                          </Typography>
                        </>
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{detail.description || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#4caf50' }}>
                      {detail.allocatedAmount.toFixed(3)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold', bgcolor: 'rgba(76,175,80,0.1)', fontSize: '0.85rem' }}>
                    {isRTL ? 'إجمالي المدين:' : 'Total Debit:'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'rgba(76,175,80,0.1)' }}>
                    <Typography variant="h6" sx={{ color: '#4caf50' }}>{totalDebit.toFixed(3)}</Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Credit Section */}
        <Paper
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: isDarkMode ? 'rgba(255,152,0,0.1)' : 'white'
          }}
        >
          <Box sx={{ bgcolor: isDarkMode ? 'rgba(255,152,0,0.3)' : '#ff9800', color: 'white', p: 1.5, fontWeight: 'bold' }}>
            {isRTL ? '📤 الدائن' : '📤 Credit'}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الحساب' : 'Account'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'مركز التكلفة' : 'Cost Center'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الوصف' : 'Description'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'المبلغ' : 'Amount'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditDetails.map((detail, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <strong>{detail.accountCode}</strong>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {isRTL ? detail.accountNameAr : detail.accountName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {detail.costCenterCode ? (
                        <>
                          {detail.costCenterCode}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {isRTL ? detail.costCenterNameAr : detail.costCenterName}
                          </Typography>
                        </>
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{detail.description || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#ff9800' }}>
                      {detail.allocatedAmount.toFixed(3)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold', bgcolor: 'rgba(255,152,0,0.1)', fontSize: '0.85rem' }}>
                    {isRTL ? 'إجمالي الدائن:' : 'Total Credit:'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'rgba(255,152,0,0.1)' }}>
                    <Typography variant="h6" sx={{ color: '#ff9800' }}>{totalCredit.toFixed(3)}</Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Balance Check */}
        <Paper
          elevation={3}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: Math.abs(totalDebit - totalCredit) < 0.01
              ? (isDarkMode ? 'rgba(76,175,80,0.2)' : '#e8f5e9')
              : (isDarkMode ? 'rgba(244,67,54,0.2)' : '#ffebee')
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              {isRTL ? 'التوازن' : 'Balance'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography variant="h6">
                {totalDebit.toFixed(3)} = {totalCredit.toFixed(3)}
              </Typography>
              <Typography variant="h4">
                {Math.abs(totalDebit - totalCredit) < 0.01 ? '✅' : '❌'}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={onClose} size="large">
          {isRTL ? 'إغلاق' : 'Close'}
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          size="large"
          sx={{
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' }
          }}
        >
          {isRTL ? 'طباعة' : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoucherPreviewDialog;